import { useState } from "react";
import { X } from "lucide-react";
import { toast } from "@/components/feedback/toast";
import { adminFetch } from "@/api";

export interface FeedbackActionsBarProps {
  feedbackId: string;
  currentStatus: string;
}

const NEXT_STATUS: Record<string, { next: string; label: string }[]> = {
  new: [
    { next: "reviewed", label: "Mark reviewed" },
    { next: "resolved", label: "Resolve" },
  ],
  reviewed: [
    { next: "resolved", label: "Resolve" },
    { next: "new", label: "Reopen" },
  ],
  resolved: [{ next: "new", label: "Reopen" }],
};

export function FeedbackActionsBar({
  feedbackId,
  currentStatus,
}: FeedbackActionsBarProps) {
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<{ next: string; label: string } | null>(
    null,
  );
  const [message, setMessage] = useState("");
  const transitions = NEXT_STATUS[currentStatus] ?? [];

  const submit = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      const res = await adminFetch(`/api/feedback/${feedbackId}/status`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ status: pending.next, message }),
      });
      if (!res.ok) {
        toast.error("Status update failed");
        return;
      }
      toast.success(
        message.trim().length > 0
          ? `Marked as ${pending.next} with note`
          : `Marked as ${pending.next}`,
      );
      setPending(null);
      setMessage("");
      window.location.reload();
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        {transitions.map((t) => (
          <button
            key={t.next}
            type="button"
            onClick={() => setPending(t)}
            disabled={busy}
            className={
              t.next === "resolved"
                ? "inline-flex items-center gap-2 rounded-md bg-gradient-to-b from-fc-brand to-emerald-600 px-3 py-1.5 text-[12.5px] font-semibold text-emerald-950 hover:brightness-105 disabled:opacity-60"
                : "rounded-md border border-fc-border bg-fc-overlay px-3 py-1.5 text-[12.5px] text-fc-text hover:border-fc-brand disabled:opacity-60"
            }
          >
            {t.label}
          </button>
        ))}
        {transitions.length === 0 ? (
          <span className="text-[12px] text-fc-text-dim">
            No status transitions available
          </span>
        ) : null}
      </div>

      {pending && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="relative w-full max-w-md rounded-2xl border border-fc-border bg-fc-raised p-6 space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-[16px] font-semibold text-fc-text">
                  {pending.label}
                </h2>
                <p className="text-[12px] text-fc-text-dim mt-0.5">
                  Add an optional note. Saved to history alongside the status
                  change.
                </p>
              </div>
              <button
                onClick={() => {
                  setPending(null);
                  setMessage("");
                }}
                className="w-8 h-8 rounded-lg text-fc-text-dim hover:text-fc-text hover:bg-fc-overlay flex items-center justify-center transition-all"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              placeholder="e.g. Shipped in v1.4.2 — thanks for reporting"
              maxLength={2000}
              className="w-full px-3 py-2 rounded-xl bg-fc-surface border border-fc-border text-[13px] text-fc-text placeholder:text-fc-text-faint focus:outline-none focus:border-fc-brand resize-none"
              autoFocus
            />
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setPending(null);
                  setMessage("");
                }}
                disabled={busy}
                className="flex-1 py-2.5 rounded-xl bg-fc-surface border border-fc-border text-[13px] text-fc-text-secondary hover:text-fc-text hover:bg-fc-overlay transition-all disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={busy}
                className="flex-1 py-2.5 rounded-xl bg-fc-brand text-black text-[13px] font-semibold hover:brightness-105 disabled:opacity-40 transition-all"
              >
                {busy ? "Saving…" : pending.label}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
